import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, RefreshCw, Search, X, Pencil, Trash2, CalendarDays, Eye, Users, Flame, Coffee, CalendarOff, Mail, Tag,
} from 'lucide-react';
import HxSelect from '../../../components/ui/HxSelect';
import { getInstructors, createInstructor, updateInstructor, deleteInstructor, getSchedules } from '../services';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import toast from 'react-hot-toast';
import { viewAsUrl } from '../../../utils/viewAs';

// ============================================================================
// Instructores del Calendario — estilo beta.hingenia.com.
// Carga calculada en el cliente con una sola lectura de eventos (antes se hacía
// una consulta por instructor). Agrega correo y especialidades.
// ============================================================================

const DAYS = [
    { key: 'monday', label: 'Lunes', short: 'L' },
    { key: 'tuesday', label: 'Martes', short: 'M' },
    { key: 'wednesday', label: 'Miércoles', short: 'X' },
    { key: 'thursday', label: 'Jueves', short: 'J' },
    { key: 'friday', label: 'Viernes', short: 'V' },
    { key: 'saturday', label: 'Sábado', short: 'S' },
    { key: 'sunday', label: 'Domingo', short: 'D' },
];

const ESTADOS = [
    { value: 'nuevo', label: 'Nuevo', tone: 'st-rec' },
    { value: 'activo', label: 'Activo', tone: 't-green' },
    { value: 'mixto', label: 'Mixto', tone: 'st-draft' },
    { value: 'saturado', label: 'Saturado', tone: 'st-live' },
    { value: 'descanso', label: 'Descanso', tone: 'st-int' },
];

const COLORS = ['#0a5cff', '#7c3aed', '#db2777', '#dc2626', '#ea580c', '#d97706', '#16a34a', '#0d9488', '#0891b2', '#475569'];

// Opciones de hora en intervalos de 30 min (formato 12 hrs)
const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
    for (let m of ['00', '30']) {
        const hour12 = h % 12 || 12;
        const ampm = h < 12 ? 'AM' : 'PM';
        TIME_OPTIONS.push({ val: `${String(h).padStart(2, '0')}:${m}`, label: `${hour12}:${m} ${ampm}` });
    }
}

const EMPTY_FORM = {
    name: '',
    email: '',
    color: '#0a5cff',
    specialties: [],
    availability: {},
    estado: 'activo',
    maxCursos: 4,
    maxEventosDia: 3,
    diasBloqueados: [],
};

// Las fechas bloqueadas se guardan como 'YYYY-MM-DD'; se leen en hora local.
const parseDay = (s) => {
    if (!s) return null;
    const [y, m, d] = String(s).split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d) : new Date(s);
};
const fmtDay = (s) => parseDay(s)?.toLocaleDateString('es', { day: 'numeric', month: 'short' }) || s;
const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

const InstructorsPage = () => {
    const navigate = useNavigate();
    const [instructors, setInstructors] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [newBlockedDate, setNewBlockedDate] = useState({ fecha: '', motivo: '' });
    const [tagDraft, setTagDraft] = useState('');

    const [tab, setTab] = useState('todos');
    const [q, setQ] = useState('');
    const [specFilter, setSpecFilter] = useState('all');

    useEffect(() => { loadInstructors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadInstructors = async () => {
        setLoading(true);
        try {
            const data = await getInstructors();

            // Usuarios con rol Instructor (Admin) sin ficha aún → se crea su ficha
            const usersSnap = await getDocs(query(collection(db, 'users'), where('isInstructor', '==', true)));
            const instructorUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            let synced = 0;
            for (const u of instructorUsers) {
                if (data.find(i => i.email === u.email)) continue;
                await createInstructor({
                    name: u.displayName || u.email?.split('@')[0] || 'Instructor',
                    email: u.email,
                    color: COLORS[Math.floor(Math.random() * COLORS.length)],
                    estado: 'activo',
                    maxCursos: 4,
                    maxEventosDia: 3,
                    availability: {},
                    diasBloqueados: [],
                    linkedUserId: u.id,
                    photoURL: u.photoURL || null,
                });
                synced++;
            }
            if (synced > 0) toast.success(`${synced} instructor(es) sincronizado(s)`);

            const [finalData, sch] = await Promise.all([
                synced > 0 ? getInstructors() : Promise.resolve(data),
                getSchedules().catch(() => []),
            ]);
            setInstructors(finalData);
            setSchedules(sch);
        } catch (error) {
            console.error(error);
            toast.error('Error cargando instructores');
        } finally {
            setLoading(false);
        }
    };

    // Carga por instructor: cursos distintos con eventos por venir, total de
    // eventos por venir y el día más cargado (para comparar con maxEventosDia).
    const loads = useMemo(() => {
        const now = new Date();
        const map = {};
        schedules.forEach(s => {
            if (!s.instructorId || s.status === 'cancelled') return;
            if (!(s.end instanceof Date) || Number.isNaN(s.end.getTime()) || s.end < now) return;
            const l = map[s.instructorId] || (map[s.instructorId] = { courses: new Set(), events: 0, perDay: {} });
            if (s.courseId) l.courses.add(s.courseId);
            l.events++;
            const k = s.start?.toDateString?.() || '';
            l.perDay[k] = (l.perDay[k] || 0) + 1;
        });
        return Object.fromEntries(Object.entries(map).map(([id, l]) => [id, {
            cursos: l.courses.size,
            eventos: l.events,
            pico: Math.max(0, ...Object.values(l.perDay)),
        }]));
    }, [schedules]);

    const enriched = useMemo(() => {
        const t0 = todayStart();
        return instructors.map(i => {
            const load = loads[i.id] || { cursos: 0, eventos: 0, pico: 0 };
            const max = Number(i.maxCursos) || 4;
            const pct = Math.round((load.cursos / max) * 100);
            const saturado = i.estado === 'saturado' || pct >= 100;
            const bloqueos = (i.diasBloqueados || [])
                .filter(b => parseDay(b.fecha) >= t0)
                .sort((a, b) => parseDay(a.fecha) - parseDay(b.fecha));
            return { ...i, load, max, pct, saturado, bloqueos };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [instructors, loads]);

    const allSpecs = useMemo(() => [...new Set(instructors.flatMap(i => i.specialties || []))].sort(), [instructors]);

    const counts = useMemo(() => {
        const in30 = new Date(todayStart().getTime() + 30 * 864e5);
        return {
            todos: enriched.length,
            activos: enriched.filter(i => i.estado !== 'descanso').length,
            saturados: enriched.filter(i => i.saturado).length,
            descanso: enriched.filter(i => i.estado === 'descanso').length,
            bloqueos30: enriched.reduce((n, i) => n + i.bloqueos.filter(b => parseDay(b.fecha) <= in30).length, 0),
        };
    }, [enriched]);

    const visible = useMemo(() => {
        const term = q.trim().toLowerCase();
        return enriched.filter(i => {
            if (tab === 'activos' && i.estado === 'descanso') return false;
            if (tab === 'saturados' && !i.saturado) return false;
            if (tab === 'descanso' && i.estado !== 'descanso') return false;
            if (specFilter !== 'all' && !(i.specialties || []).includes(specFilter)) return false;
            if (!term) return true;
            return `${i.name} ${i.email || ''} ${(i.specialties || []).join(' ')}`.toLowerCase().includes(term);
        });
    }, [enriched, tab, q, specFilter]);

    // ── Formulario ─────────────────────────────────────────────
    const openNew = () => {
        setEditingId(null);
        setFormData(EMPTY_FORM);
        setNewBlockedDate({ fecha: '', motivo: '' });
        setTagDraft('');
        setDrawerOpen(true);
    };

    const handleEdit = (inst) => {
        setEditingId(inst.id);
        setFormData({
            name: inst.name,
            email: inst.email || '',
            color: inst.color || '#0a5cff',
            specialties: inst.specialties || [],
            availability: inst.availability || {},
            estado: inst.estado || 'activo',
            maxCursos: inst.maxCursos || 4,
            maxEventosDia: inst.maxEventosDia || 3,
            diasBloqueados: inst.diasBloqueados || [],
        });
        setNewBlockedDate({ fecha: '', motivo: '' });
        setTagDraft('');
        setDrawerOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            const payload = {
                ...formData,
                // Sin pasar a minúsculas: Sincronizar compara el correo tal cual con users.email
                email: (formData.email || '').trim(),
                maxCursos: Number(formData.maxCursos),
                maxEventosDia: Number(formData.maxEventosDia),
            };
            if (editingId) {
                await updateInstructor(editingId, payload);
                toast.success('Instructor actualizado');
            } else {
                await createInstructor(payload);
                toast.success('Instructor creado');
            }
            setDrawerOpen(false);
            loadInstructors();
        } catch (error) {
            console.error(error);
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (inst) => {
        if (!window.confirm(`¿Seguro que deseas eliminar a ${inst.name}?`)) return;
        try {
            await deleteInstructor(inst.id);
            toast.success('Instructor eliminado');
            loadInstructors();
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const set = (k) => (e) => setFormData(f => ({ ...f, [k]: e.target.value }));

    const toggleDay = (dayKey) => setFormData(prev => {
        const av = { ...prev.availability };
        if (av[dayKey]) delete av[dayKey]; else av[dayKey] = ['09:00-18:00'];
        return { ...prev, availability: av };
    });
    const setSlot = (dayKey, start, end) => setFormData(prev => ({
        ...prev, availability: { ...prev.availability, [dayKey]: [`${start}-${end}`] },
    }));

    const addTag = () => {
        const t = tagDraft.trim();
        if (!t) return;
        setFormData(f => (f.specialties.some(s => s.toLowerCase() === t.toLowerCase()) ? f : { ...f, specialties: [...f.specialties, t] }));
        setTagDraft('');
    };
    const removeTag = (t) => setFormData(f => ({ ...f, specialties: f.specialties.filter(s => s !== t) }));

    const addBlockedDate = () => {
        if (!newBlockedDate.fecha) return;
        setFormData(prev => ({ ...prev, diasBloqueados: [...prev.diasBloqueados, { ...newBlockedDate }] }));
        setNewBlockedDate({ fecha: '', motivo: '' });
    };
    const removeBlockedDate = (index) => setFormData(prev => ({
        ...prev, diasBloqueados: prev.diasBloqueados.filter((_, i) => i !== index),
    }));

    // ── Celdas ────────────────────────────────────────────────
    const estadoPill = (i) => {
        const cfg = ESTADOS.find(e => e.value === i.estado) || ESTADOS[1];
        if (i.saturado && i.estado !== 'saturado') return <span className="pill st-live">Saturado</span>;
        return <span className={`pill ${cfg.tone}`}>{cfg.label}</span>;
    };
    const loadCell = (i) => {
        const color = i.pct >= 100 ? 'var(--bad)' : i.pct >= 75 ? 'var(--warn)' : 'var(--ok)';
        return (
            <div style={{ minWidth: 130 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                    <b style={{ fontWeight: 600 }}>{i.load.cursos}/{i.max} cursos</b>
                    <span className="muted">{i.pct}%</span>
                </div>
                <div className="bar"><i style={{ width: `${Math.min(i.pct, 100)}%`, background: color }} /></div>
            </div>
        );
    };
    const eventsCell = (i) => (
        <div style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
            <b style={{ fontWeight: 600 }}>{i.load.eventos}</b> <span className="muted">por venir</span>
            <div className="muted" style={{ fontSize: 12 }}>
                Pico <span style={{ color: i.load.pico > (i.maxEventosDia || 3) ? 'var(--bad)' : 'inherit', fontWeight: 600 }}>{i.load.pico}</span>/{i.maxEventosDia || 3} por día
            </div>
        </div>
    );
    const weekCell = (i) => (
        <div className="wk" style={{ width: 168 }}>
            {DAYS.map(d => {
                const slot = i.availability?.[d.key]?.[0];
                return <span key={d.key} className={slot ? 'on' : ''} title={slot ? `${d.label}: ${slot.replace('-', ' – ')}` : `${d.label}: no disponible`}>{d.short}</span>;
            })}
        </div>
    );
    const blockCell = (i) => {
        if (!i.bloqueos.length) return <span className="muted">—</span>;
        const b = i.bloqueos[0];
        return (
            <span style={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                <span className="pill t-red">{fmtDay(b.fecha)}</span>
                {i.bloqueos.length > 1 && <span className="muted"> +{i.bloqueos.length - 1}</span>}
            </span>
        );
    };

    return (
        <div className={`hx-split${drawerOpen ? ' has-panel' : ''}`}>
        <div className="hx-page">
            <div className="crumb">Calendario › Instructores</div>
            <div className="p-head">
                <div>
                    <h1>Instructores</h1>
                    <p>Carga de trabajo, disponibilidad y días bloqueados.</p>
                </div>
                <div className="acts">
                    <button type="button" className="btn" onClick={loadInstructors} title="Crea la ficha de los usuarios con rol Instructor en Admin">
                        <RefreshCw size={15} /> Sincronizar
                    </button>
                    <button type="button" className="btn pri" onClick={openNew}><Plus size={16} /> Nuevo instructor</button>
                </div>
            </div>

            <div className="kpis">
                <div className="card kpi">
                    <div className="lbl"><span className="ico t-green"><Users size={15} /></span>Disponibles</div>
                    <div className="num">{loading ? '–' : counts.activos}</div>
                    <div className="hint">de {counts.todos} instructores</div>
                </div>
                <div className="card kpi">
                    <div className="lbl"><span className="ico t-red"><Flame size={15} /></span>Saturados</div>
                    <div className="num">{loading ? '–' : counts.saturados}</div>
                    <div className="hint">En su límite de cursos</div>
                </div>
                <div className="card kpi">
                    <div className="lbl"><span className="ico t-gray"><Coffee size={15} /></span>En descanso</div>
                    <div className="num">{loading ? '–' : counts.descanso}</div>
                    <div className="hint">No reciben cursos nuevos</div>
                </div>
                <div className="card kpi">
                    <div className="lbl"><span className="ico t-amber"><CalendarOff size={15} /></span>Bloqueos próximos</div>
                    <div className="num">{loading ? '–' : counts.bloqueos30}</div>
                    <div className="hint">Días bloqueados en 30 días</div>
                </div>
            </div>

            <div className="toolbar">
                <div className="tabs">
                    {[['todos', 'Todos', counts.todos], ['activos', 'Disponibles', counts.activos], ['saturados', 'Saturados', counts.saturados], ['descanso', 'Descanso', counts.descanso]].map(([k, l, n]) => (
                        <button key={k} type="button" className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l} <em>{n}</em></button>
                    ))}
                </div>
                {allSpecs.length > 0 && (
                    <HxSelect
                        value={specFilter}
                        onChange={setSpecFilter}
                        options={[
                            { value: 'all', label: 'Todas las especialidades', icon: Tag },
                            ...allSpecs.map(s => ({ value: s, label: s, count: instructors.filter(i => (i.specialties || []).includes(s)).length })),
                        ]}
                        searchPlaceholder="Buscar especialidad…"
                        icon={Tag}
                        ariaLabel="Filtrar por especialidad"
                    />
                )}
                <label className="search">
                    <Search size={15} />
                    <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, correo o especialidad…" />
                    {q && <button type="button" className="ibtn" style={{ width: 22, height: 22 }} onClick={() => setQ('')} aria-label="Limpiar"><X size={13} /></button>}
                </label>
            </div>

            {loading ? (
                <div className="card"><div className="skel" /><div className="skel" /><div className="skel" /></div>
            ) : visible.length === 0 ? (
                <div className="card empty">{q || specFilter !== 'all' ? 'Ningún instructor coincide con los filtros.' : 'No hay instructores en esta pestaña.'}</div>
            ) : (
                <div className="card tbl-wrap">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>Instructor</th>
                                <th className="wide-only">Especialidades</th>
                                <th>Estado</th>
                                <th>Carga</th>
                                <th className="wide-only">Eventos</th>
                                <th>Disponibilidad</th>
                                <th className="wide-only">Bloqueos</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map(i => (
                                <tr key={i.id} className={`click${drawerOpen && editingId === i.id ? ' is-sel' : ''}`} onClick={() => handleEdit(i)}>
                                    <td>
                                        <div className="ttl">
                                            {i.photoURL
                                                ? <img className="sq" src={i.photoURL} alt="" style={{ borderRadius: '50%', objectFit: 'cover', boxShadow: `0 0 0 2px ${i.color || '#94a3b8'}` }} />
                                                : <span className="sq" style={{ background: i.color || '#94a3b8', borderRadius: '50%' }}>{i.name.charAt(0).toUpperCase()}</span>}
                                            <div>
                                                <b>{i.name}</b>
                                                <small>{i.email || 'Sin correo'}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="wide-only">
                                        {(i.specialties || []).length
                                            ? <div className="chips" style={{ maxWidth: 220 }}>{i.specialties.map(s => <span key={s} className="chip">{s}</span>)}</div>
                                            : <span className="muted">—</span>}
                                    </td>
                                    <td>{estadoPill(i)}</td>
                                    <td>{loadCell(i)}</td>
                                    <td className="wide-only">{eventsCell(i)}</td>
                                    <td>{weekCell(i)}</td>
                                    <td className="wide-only">{blockCell(i)}</td>
                                    <td className="r">
                                        <div className="row-acts" onClick={e => e.stopPropagation()}>
                                            <button type="button" className="ibtn" title="Ver como instructor (lo que él ve)" onClick={() => navigate(viewAsUrl(i.id))}><Eye size={15} /></button>
                                            <button type="button" className="ibtn" title="Ver su calendario" onClick={() => navigate(`/mi-calendario?id=${i.id}`)}><CalendarDays size={15} /></button>
                                            <button type="button" className="ibtn" title="Editar" onClick={() => handleEdit(i)}><Pencil size={15} /></button>
                                            <button type="button" className="ibtn bad" title="Eliminar" onClick={() => handleDelete(i)}><Trash2 size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </div>

            {/* Panel de edición a la derecha (divide la pantalla, sin popup) */}
            {drawerOpen && (
                    <form className="hx-drawer hx-panel" onSubmit={handleSubmit}>
                        <div className="d-head">
                            <span style={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', background: formData.color, color: '#fff', fontWeight: 800, flexShrink: 0 }}>
                                {(formData.name || '?').charAt(0).toUpperCase()}
                            </span>
                            <div>
                                <h2>{editingId ? 'Editar instructor' : 'Nuevo instructor'}</h2>
                                <p>{editingId ? formData.name : 'Se podrá asignar a cursos del calendario'}</p>
                            </div>
                            <button type="button" className="ibtn x" onClick={() => setDrawerOpen(false)} aria-label="Cerrar"><X size={18} /></button>
                        </div>

                        <div className="d-body">
                            <div className="sec">
                                <div className="sec-h">Datos</div>
                                <label className="f">
                                    <span>Nombre</span>
                                    <input className="inp" required value={formData.name} onChange={set('name')} placeholder="Ej. Ing. Carlos Pingo" />
                                </label>
                                <label className="f">
                                    <span>Correo <small>— el mismo de su cuenta, para vincular su portal de instructor</small></span>
                                    <div style={{ position: 'relative' }}>
                                        <Mail size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--ink-3)' }} />
                                        <input className="inp" type="email" value={formData.email} onChange={set('email')} placeholder="instructor@correo.com" style={{ paddingLeft: 34 }} />
                                    </div>
                                </label>
                                <div className="f">
                                    <span>Especialidades <small>— Enter para agregar</small></span>
                                    <div className="tag-in">
                                        {formData.specialties.map(t => (
                                            <span key={t} className="tag">{t}<button type="button" onClick={() => removeTag(t)} aria-label={`Quitar ${t}`}><X size={12} /></button></span>
                                        ))}
                                        <input
                                            value={tagDraft}
                                            onChange={e => setTagDraft(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
                                                else if (e.key === 'Backspace' && !tagDraft && formData.specialties.length) removeTag(formData.specialties[formData.specialties.length - 1]);
                                            }}
                                            onBlur={addTag}
                                            placeholder={formData.specialties.length ? '' : 'Revit, MS Project, Delphin…'}
                                            list="hx-specs"
                                        />
                                        <datalist id="hx-specs">{allSpecs.map(s => <option key={s} value={s} />)}</datalist>
                                    </div>
                                </div>
                                <div className="f">
                                    <span>Estado</span>
                                    <div className="opt">
                                        {ESTADOS.map(e => (
                                            <button key={e.value} type="button" className={formData.estado === e.value ? 'on' : ''} onClick={() => setFormData(f => ({ ...f, estado: e.value }))}>{e.label}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="f">
                                    <span>Color en el calendario</span>
                                    <div className="swatches">
                                        {COLORS.map(c => (
                                            <button key={c} type="button" className={formData.color === c ? 'on' : ''} style={{ background: c }} onClick={() => setFormData(f => ({ ...f, color: c }))} aria-label={`Color ${c}`} />
                                        ))}
                                        <input type="color" value={formData.color} onChange={set('color')} title="Otro color" style={{ width: 30, height: 30, border: 0, background: 'none', padding: 0, cursor: 'pointer' }} />
                                    </div>
                                </div>
                            </div>

                            <div className="sec">
                                <div className="sec-h">Límites</div>
                                <div className="two">
                                    <label className="f"><span>Máx. cursos simultáneos</span>
                                        <input className="inp" type="number" min="1" max="20" value={formData.maxCursos} onChange={set('maxCursos')} />
                                    </label>
                                    <label className="f"><span>Máx. eventos por día</span>
                                        <input className="inp" type="number" min="1" max="10" value={formData.maxEventosDia} onChange={set('maxEventosDia')} />
                                    </label>
                                </div>
                            </div>

                            <div className="sec">
                                <div className="sec-h">Disponibilidad semanal</div>
                                {DAYS.map(day => {
                                    const slot = formData.availability[day.key]?.[0];
                                    const [start, end] = (slot || '09:00-18:00').split('-');
                                    return (
                                        <div key={day.key} className="day">
                                            <label>
                                                <input type="checkbox" checked={!!slot} onChange={() => toggleDay(day.key)} />
                                                <span style={{ color: slot ? 'var(--ink)' : 'var(--ink-3)' }}>{day.label}</span>
                                            </label>
                                            {slot ? (
                                                <div className="rng">
                                                    <select className="inp" value={start} onChange={e => setSlot(day.key, e.target.value, end)}>
                                                        {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                                                    </select>
                                                    a
                                                    <select className="inp" value={end} onChange={e => setSlot(day.key, start, e.target.value)}>
                                                        {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                                                    </select>
                                                </div>
                                            ) : <span className="muted" style={{ fontSize: 12.5 }}>No disponible</span>}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="sec">
                                <div className="sec-h">Días bloqueados</div>
                                <div className="two" style={{ gridTemplateColumns: '150px 1fr auto', alignItems: 'end' }}>
                                    <label className="f" style={{ marginBottom: 8 }}><span>Fecha</span>
                                        <input className="inp" type="date" value={newBlockedDate.fecha} onChange={e => setNewBlockedDate(b => ({ ...b, fecha: e.target.value }))} />
                                    </label>
                                    <label className="f" style={{ marginBottom: 8 }}><span>Motivo</span>
                                        <input className="inp" value={newBlockedDate.motivo} onChange={e => setNewBlockedDate(b => ({ ...b, motivo: e.target.value }))}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBlockedDate(); } }} placeholder="Vacaciones, viaje…" />
                                    </label>
                                    <button type="button" className="btn" style={{ marginBottom: 8 }} onClick={addBlockedDate} disabled={!newBlockedDate.fecha}><Plus size={15} /> Agregar</button>
                                </div>
                                {formData.diasBloqueados.length === 0 ? (
                                    <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0', color: 'var(--ink-3)' }}>Sin días bloqueados.</p>
                                ) : (
                                    formData.diasBloqueados.map((b, idx) => {
                                        const past = parseDay(b.fecha) < todayStart();
                                        return (
                                            <div key={`${b.fecha}-${idx}`} className="blk" style={past ? { opacity: .55 } : undefined}>
                                                <CalendarOff size={15} style={{ color: 'var(--bad)' }} />
                                                <b style={{ fontWeight: 600 }}>{parseDay(b.fecha)?.toLocaleDateString('es', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</b>
                                                <small>{b.motivo || 'Sin motivo'}{past ? ' · pasado' : ''}</small>
                                                <button type="button" className="ibtn" style={{ marginLeft: 'auto' }} onClick={() => removeBlockedDate(idx)} aria-label="Quitar"><X size={14} /></button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div className="d-foot">
                            <button type="button" className="btn" onClick={() => setDrawerOpen(false)}>Cerrar</button>
                            <button type="submit" className="btn pri" disabled={saving}>{saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear instructor'}</button>
                        </div>
                    </form>
            )}
        </div>
    );
};

export default InstructorsPage;
